import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { SERVICE_ACTIVATED_EVENT } from '../../hooks/useServiceActivationWatcher/useServiceActivationWatcher';

interface EpgModalDetailsContextType {
    getProgramDetails: (programId: string) => any | null;
    setProgramDetails: (programId: string, data: any) => void;
    invalidateProgram: (programId: string) => void;
    clearCache: () => void;
}

const EpgModalDetailsContext = createContext<EpgModalDetailsContextType | null>(null);

const programDetailsCache: Record<string, { data: any; timestamp: number }> = {};

export const useEpgModalDetailsStoreService = () => {
    const [, forceUpdate] = useState({});
    const CACHE_EXPIRATION_TIME = 10 * 60 * 1000; // Cache expiration time - 10 minutes

    const getProgramDetails = useCallback((programId: string) => {
        const cached = programDetailsCache[programId];
        if (!cached) {
            return null;
        }

        const currentTime = Date.now();
        const cacheAge = currentTime - cached.timestamp;

        if (cacheAge < CACHE_EXPIRATION_TIME) {
            return cached.data;
        }

        delete programDetailsCache[programId];
        forceUpdate({});
        return null;
    }, []);

    const setProgramDetails = useCallback((programId: string, data: any) => {
        const currentCachedData = programDetailsCache[programId]?.data;
        const isRecordedValue = data.isRecorded !== undefined ? data.isRecorded : currentCachedData?.isRecorded;

        programDetailsCache[programId] = {
            data: {
                ...data,
                isRecorded: isRecordedValue,
            },
            timestamp: Date.now(),
        };
        forceUpdate({});
    }, []);

    const invalidateProgram = useCallback((programId: string, forceDelete: boolean = false) => {
        const isRecorded = programDetailsCache[programId]?.data?.isRecorded;

        if (programDetailsCache[programId]) {
            if (forceDelete) {
                // Force delete the entire cache entry (e.g. after service upgrade)
                delete programDetailsCache[programId];
            } else if (isRecorded !== undefined) {
                programDetailsCache[programId].data = {
                    ...programDetailsCache[programId].data,
                    isRecorded,
                };
                programDetailsCache[programId].timestamp = Date.now();
            } else {
                delete programDetailsCache[programId];
            }
            forceUpdate({});
        }
    }, []);

    const clearCache = useCallback(() => {
        Object.keys(programDetailsCache).forEach((key) => {
            delete programDetailsCache[key];
        });
        forceUpdate({});
    }, []);

    useEffect(() => {
        const handleSubscriptionChange = () => clearCache();
        window.addEventListener(SERVICE_ACTIVATED_EVENT, handleSubscriptionChange);
        window.addEventListener('service-purchase-synced', handleSubscriptionChange);
        return () => {
            window.removeEventListener(SERVICE_ACTIVATED_EVENT, handleSubscriptionChange);
            window.removeEventListener('service-purchase-synced', handleSubscriptionChange);
        };
    }, [clearCache]);

    return {
        getProgramDetails,
        setProgramDetails,
        invalidateProgram,
        clearCache,
    };
};

export const EpgModalDetailsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const EpgModalDetailsStore = useEpgModalDetailsStoreService();

    return <EpgModalDetailsContext.Provider value={EpgModalDetailsStore}>{children}</EpgModalDetailsContext.Provider>;
};

export const useProgramDetailsStore = () => useContext(EpgModalDetailsContext);
